import React, { useEffect, useState } from "react";
import { api, formatMinutes } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "../hooks/use-toast";

const COLORS = ["#0ABAB5", "#3B82F6", "#8B5CF6", "#EC4899", "#F59E0B", "#10B981", "#EF4444", "#6366F1", "#F97316", "#14B8A6"];

export default function Disciplinas() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", color: COLORS[0] });

  const load = async () => {
    const [r, s] = await Promise.all([api.get("/disciplines"), api.get("/dashboard/stats")]);
    setItems(r.data);
    const map = {};
    s.data.per_discipline.forEach((d) => { map[d.id] = d; });
    setStats(map);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: "", color: COLORS[0] }); setOpen(true); };
  const openEdit = (d) => { setEditing(d); setForm({ name: d.name, color: d.color }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast({ title: "Informe o nome" }); return; }
    if (editing) {
      await api.put(`/disciplines/${editing.id}`, form);
      toast({ title: "Disciplina atualizada" });
    } else {
      await api.post("/disciplines", form);
      toast({ title: "Disciplina criada" });
    }
    setOpen(false);
    load();
  };

  const del = async (d) => {
    if (!confirm(`Excluir "${d.name}"? Todos os estudos e tópicos serão removidos.`)) return;
    await api.delete(`/disciplines/${d.id}`);
    toast({ title: "Disciplina excluída" });
    load();
  };

  return (
    <div className="space-y-6 max-w-[1400px]">
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl font-extrabold">Disciplinas</h1>
        <Button onClick={openCreate} className="bg-tiffany-500 hover:bg-tiffany-600 text-white">
          <Plus size={16} className="mr-1" /> Nova Disciplina
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center">
          <p className="text-muted-foreground mb-4">Você ainda não tem disciplinas cadastradas.</p>
          <Button onClick={openCreate} className="bg-tiffany-500 hover:bg-tiffany-600 text-white">Adicionar Primeira Disciplina</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((d) => {
            const s = stats[d.id] || { minutes: 0, correct: 0, wrong: 0, questions: 0, performance: 0 };
            return (
              <div key={d.id} className="rounded-2xl bg-card border border-border p-5 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ background: d.color }} />
                    <h3 className="font-display font-bold text-lg">{d.name}</h3>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(d)} className="p-1.5 rounded hover:bg-muted text-muted-foreground"><Pencil size={14} /></button>
                    <button onClick={() => del(d)} className="p-1.5 rounded hover:bg-red-50 text-red-500"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Stat label="Tempo" value={formatMinutes(s.minutes)} />
                  <Stat label="Questões" value={s.questions} />
                  <Stat label="Acertos" value={s.correct} color="text-green-600" />
                  <Stat label="Erros" value={s.wrong} color="text-red-500" />
                </div>
                <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Desempenho</span>
                  <span className={`font-bold ${s.performance >= 70 ? "text-green-600" : s.performance >= 50 ? "text-yellow-600" : "text-red-500"}`}>{s.performance}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar" : "Nova"} Disciplina</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex: Direito Constitucional" />
            </div>
            <div>
              <Label>Cor</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    onClick={() => setForm({ ...form, color: c })}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${form.color === c ? "border-foreground scale-110" : "border-transparent"}`}
                    style={{ background: c }}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-tiffany-500 hover:bg-tiffany-600 text-white">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, color = "" }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`font-bold ${color}`}>{value}</div>
    </div>
  );
}
